/**
 * Doctor Manager – Add, Edit, View doctors.
 * API base path: apiPath.doctors => "doctors" (full: GET/POST /api/admin/doctors, GET/PUT/DELETE /api/admin/doctors/:id)
 *
 * Payload for POST/PUT:
 *   name, email, specialization, city, latitude, longitude,
 *   country_code, mobile, status ("ACTIVE" | "INACTIVE"),
 *   optional: secondary_country_code, secondary_mobile
 *
 * Expected list item shape: { id, name, email, specialization, status, location: { city, latitude, longitude }, phone_numbers: [{ country_code, number }, ...] }
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Tooltip,
  Select,
  Switch,
  Descriptions,
  Divider,
  Typography,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import lang from "../helper/lang";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";
import LocationPicker from "../components/LocationPicker";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

function DoctorManager() {
  const { request } = useRequest();
  const isMounted = useRef(false);
  const requestRef = useRef(request);

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [countryCode, setCountryCode] = useState("");
  const [secondaryCountryCode, setSecondaryCountryCode] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ sort: "newest", status: null });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [phoneCountryIso, setPhoneCountryIso] = useState(null);
  const [secondaryPhoneCountryIso, setSecondaryPhoneCountryIso] = useState(null);

  const [markerPosition, setMarkerPosition] = useState({ lat: 25.0, lng: 71.0 });

  const initialPosition = useMemo(() => {
    if (modalType === "edit" && selectedDoctor) {
      const loc = selectedDoctor.location;
      return {
        lat: parseFloat(loc?.latitude) || 25.0,
        lng: parseFloat(loc?.longitude) || 71.0,
      };
    }
    return null;
  }, [modalType, selectedDoctor]);

  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});

  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const handleLocationSelect = useCallback(
    (locationData) => {
      const { lat, lng, city } = locationData;
      setMarkerPosition({ lat, lng });
      form.setFieldsValue({
        latitude: parseFloat(lat.toFixed(6)),
        longitude: parseFloat(lng.toFixed(6)),
        city: city || `Location at ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      });
    },
    [form],
  );

  const fetchDoctors = useCallback(() => {
    if (!isMounted.current) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.sort) params.append("sort", filters.sort);
    if (filters.status) params.append("status", filters.status);
    if (debouncedSearchText) params.append("search", debouncedSearchText);
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    requestRef.current({
      url: `/api/admin/${apiPath.doctors}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        if (!isMounted.current) return;
        setLoading(false);
        if (data?.status === 200 && data?.data) {
          setDoctors(Array.isArray(data.data) ? data.data : []);
          if (data.pagination) {
            setPagination((prev) => ({
              ...prev,
              total: data.pagination.total || 0,
              current: data.pagination.page || prev.current,
              pageSize: data.pagination.limit || prev.pageSize,
            }));
          }
        } else {
          setDoctors([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          ShowToast(data?.message || "Failed to fetch doctors", Severty.ERROR);
        }
      },
      onError: () => {
        if (!isMounted.current) return;
        setLoading(false);
        setDoctors([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast("Failed to fetch doctors", Severty.ERROR);
      },
    });
  }, [
    filters.sort,
    filters.status,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchDoctors();
  }, [
    filters.sort,
    filters.status,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
  ]);

  const handleTableChange = (paginationInfo) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddDoctor = () => {
    setModalType("add");
    form.resetFields();
    setMarkerPosition({ lat: 25.0, lng: 71.0 });
    form.setFieldsValue({
      latitude: 25.0,
      longitude: 71.0,
      city: "",
    });
    setSelectedDoctor(null);
    setModalVisible(true);
  };

  const handleEditDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setModalType("edit");
    setModalVisible(true);
    const loc = doctor.location || {};
    const lat = parseFloat(loc.latitude);
    const lng = parseFloat(loc.longitude);
    const primaryPhone = doctor.phone_numbers?.[0] || {};
    const secondaryPhone = doctor.phone_numbers?.[1] || {};
    const mobileVal = primaryPhone.number
      ? `${primaryPhone.country_code || ""}${primaryPhone.number}`.replace(/^\+/, "")
      : "";
    const secondaryMobileVal = secondaryPhone.number
      ? `${secondaryPhone.country_code || ""}${secondaryPhone.number}`.replace(/^\+/, "")
      : "";

    form.setFieldsValue({
      name: doctor.name,
      email: doctor.email,
      specialization: doctor.specialization,
      city: loc.city || "",
      latitude: lat,
      longitude: lng,
      mobile: mobileVal,
      country_code: primaryPhone.country_code || "",
      secondary_mobile: secondaryMobileVal || undefined,
      secondary_country_code: secondaryPhone.country_code || undefined,
      status: doctor.status === "ACTIVE",
    });
    setCountryCode(primaryPhone.country_code || "");
    setSecondaryCountryCode(secondaryPhone.country_code || "");
    setMarkerPosition({ lat: lat || 25.0, lng: lng || 71.0 });
  };

  const handleViewDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setViewModalVisible(true);
  };

  const getMapUrl = (doctor) => {
    const loc = doctor.location;
    if (!loc?.latitude || !loc?.longitude) return null;
    return `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
  };

  const isValidPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return false;
    const phoneNumber = parsePhoneNumberFromString(
      phone,
      countryCode.toUpperCase(),
    );
    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const getPhoneExample = (countryCode) => {
    try {
      const example = getExampleNumber(countryCode, examples);
      if (!example) return null;
      const intl = example.formatInternational();
      let seenCountryCode = false;
      return intl.replace(/\d+/g, (match) => {
        if (!seenCountryCode) {
          seenCountryCode = true;
          return match;
        }
        return "x".repeat(match.length);
      });
    } catch {
      return null;
    }
  };

  const buildPayload = (values) => {
    const rawMobile = (values.mobile || "").replace(/\D/g, "");
    const cleanMobile =
      countryCode && rawMobile.startsWith(countryCode)
        ? rawMobile.slice(countryCode.length)
        : rawMobile;

    const rawSecondary = (values.secondary_mobile || "").replace(/\D/g, "");
    const cleanSecondary =
      secondaryCountryCode && rawSecondary.startsWith(secondaryCountryCode)
        ? rawSecondary.slice(secondaryCountryCode.length)
        : rawSecondary;

    const payload = {
      name: values.name,
      email: values.email,
      specialization: values.specialization,
      city: values.city,
      latitude: parseFloat(values.latitude),
      longitude: parseFloat(values.longitude),
      country_code: countryCode,
      mobile: cleanMobile,
      status: values.status ? "ACTIVE" : "INACTIVE",
    };
    if (values.secondary_mobile && secondaryCountryCode) {
      payload.secondary_country_code = secondaryCountryCode;
      payload.secondary_mobile = cleanSecondary;
    }
    return payload;
  };

  const handleFormSubmit = async (values) => {
    try {
      const payload = buildPayload(values);
      if (modalType === "add") {
        setCreateLoading(true);
        requestRef.current({
          url: `/api/admin/${apiPath.doctors}`,
          method: "POST",
          data: payload,
          onSuccess: (response) => {
            if (!isMounted.current) return;
            if (response?.status === 201) {
              ShowToast(
                response?.data?.message || "Doctor created successfully",
                Severty.SUCCESS,
              );
              setModalVisible(false);
              setPhoneCountryIso(null);
              setSecondaryPhoneCountryIso(null);
              fetchDoctors();
            } else {
              ShowToast(
                response?.data?.message || "Failed to create doctor",
                Severty.ERROR,
              );
            }
            setCreateLoading(false);
          },
          onError: (error) => {
            if (!isMounted.current) return;
            ShowToast(
              error?.response?.data?.message || "Failed to create doctor",
              Severty.ERROR,
            );
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        requestRef.current({
          url: `/api/admin/${apiPath.doctors}/${selectedDoctor.id}`,
          method: "PUT",
          data: payload,
          onSuccess: (data) => {
            if (!isMounted.current) return;
            if (data?.status === 200) {
              ShowToast("Doctor updated successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchDoctors();
            } else {
              ShowToast(data?.message || "Failed to update doctor", Severty.ERROR);
            }
            setUpdateLoading(false);
          },
          onError: () => {
            if (!isMounted.current) return;
            ShowToast("Failed to update doctor", Severty.ERROR);
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save doctor", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
    }
  };

  const handleDeleteDoctor = (doctor) => {
    setDeleteLoading((prev) => ({ ...prev, [doctor.id]: true }));
    requestRef.current({
      url: `/api/admin/${apiPath.doctors}/${doctor.id}`,
      method: "DELETE",
      onSuccess: () => {
        if (!isMounted.current) return;
        ShowToast("Doctor deleted successfully", Severty.SUCCESS);
        setDeleteLoading((prev) => ({ ...prev, [doctor.id]: false }));
        setViewModalVisible(false);
        fetchDoctors();
      },
      onError: () => {
        if (!isMounted.current) return;
        ShowToast("Failed to delete doctor", Severty.ERROR);
        setDeleteLoading((prev) => ({ ...prev, [doctor.id]: false }));
      },
    });
  };

  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text code>DR_{id}</Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (name) => <strong>{name || "N/A"}</strong>,
    },
    {
      title: "Specialization",
      dataIndex: "specialization",
      key: "specialization",
      render: (s) => <span>{s || "N/A"}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => <span>{email || "N/A"}</span>,
    },
    {
      title: "Location",
      key: "location",
      render: (_, record) => {
        const url = getMapUrl(record);
        const city = record.location?.city;
        if (!url) return <span>{city || "N/A"}</span>;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {city ? `${city} (Map)` : "View on Map"}
          </a>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "ACTIVE" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDoctor(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditDoctor(record)}
              disabled={deleteLoading[record.id]}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={deleteLoading[record.id]}
              onClick={() => {
                Modal.confirm({
                  title: "Delete Doctor",
                  content: "Are you sure you want to delete this doctor?",
                  okText: "Yes",
                  okType: "danger",
                  cancelText: "No",
                  onOk: () => handleDeleteDoctor(record),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
        <Card
          title={
            <>
              <span>Doctors</span>
              <p>Manage doctors in the system</p>
            </>
          }
          extra={
            <Space wrap>
              <Search
                placeholder="Search doctors"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                style={{ width: 200 }}
              />
              <Select
                placeholder="Sort By"
                style={{ width: 120 }}
                value={filters.sort}
                onChange={(value) => handleFilterChange("sort", value)}
              >
                <Option value="newest">Newest</Option>
                <Option value="oldest">Oldest</Option>
              </Select>
              <Select
                placeholder="Status"
                style={{ width: 120 }}
                allowClear
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
              >
                <Option value="ACTIVE">Active</Option>
                <Option value="INACTIVE">Inactive</Option>
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDoctor}
              >
                Add Doctor
              </Button>
            </Space>
          }
        >
          <div className="table-responsive">
            <Table
              columns={getColumns()}
              dataSource={doctors}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total || 0,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} doctors`,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        </Card>

        {/* View Doctor Modal */}
        <Modal
          title="Doctor Details"
          open={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setViewModalVisible(false);
                if (selectedDoctor) handleEditDoctor(selectedDoctor);
              }}
            >
              Edit Profile
            </Button>,
            <Button
              key="delete"
              danger
              icon={<DeleteOutlined />}
              loading={selectedDoctor && deleteLoading[selectedDoctor.id]}
              onClick={() => {
                if (!selectedDoctor) return;
                Modal.confirm({
                  title: "Delete Doctor",
                  content: "Are you sure you want to delete this doctor?",
                  okText: "Yes",
                  okType: "danger",
                  cancelText: "No",
                  onOk: () => handleDeleteDoctor(selectedDoctor),
                });
              }}
            >
              Delete Profile
            </Button>,
          ]}
          width={560}
        >
          {selectedDoctor && (
            <>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Name">
                  <strong>{selectedDoctor.name}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Specialization">
                  {selectedDoctor.specialization || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  <Space>
                    <MailOutlined />
                    {selectedDoctor.email || "N/A"}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <Space direction="vertical" size={0}>
                    {selectedDoctor.primary_number && (
                      <span>
                        <PhoneOutlined /> {selectedDoctor.primary_number}
                      </span>
                    )}
                    {selectedDoctor.secondary_number && (
                      <span>
                        <PhoneOutlined /> {selectedDoctor.secondary_number}
                      </span>
                    )}
                    {!selectedDoctor.primary_number && !selectedDoctor.secondary_number && "N/A"}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Location">
                  {getMapUrl(selectedDoctor) ? (
                    <a
                      href={getMapUrl(selectedDoctor)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <EnvironmentOutlined /> {selectedDoctor.location?.city || "View on Map"}
                    </a>
                  ) : (
                    selectedDoctor.location?.city || "N/A"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag
                    color={
                      selectedDoctor.status === "ACTIVE" ? "green" : "red"
                    }
                  >
                    {selectedDoctor.status}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </>
          )}
        </Modal>

        {/* Add/Edit Doctor Modal */}
        <Modal
          title={modalType === "add" ? "Add Doctor" : "Edit Doctor"}
          open={modalVisible}
          onCancel={() =>
            !createLoading && !updateLoading && setModalVisible(false)
          }
          footer={null}
          width={700}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={async (values) => {
              try {
                await form.validateFields();
                handleFormSubmit(values);
              } catch (err) {}
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Doctor Name"
                  rules={[
                    { required: true, message: "Please enter doctor name" },
                    { max: 100, message: "Max 100 characters" },
                    {
                      pattern: /^[A-Za-z0-9 ._-]+$/,
                      message: "Invalid characters",
                    },
                  ]}
                >
                  <Input placeholder="e.g. Dr. Ravi Sadana" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="specialization"
                  label="Specialization"
                  rules={[
                    { required: true, message: "Please enter specialization" },
                    { max: 100, message: "Max 100 characters" },
                  ]}
                >
                  <Input placeholder="e.g. Gastrosurgeon" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    {
                      required: true,
                      message: lang("Please_enter_email") || "Please enter email",
                    },
                    {
                      validator(_, value) {
                        if (!value) return Promise.resolve();
                        const emailRegex =
                          /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@[a-zA-Z0-9-]+\.[a-zA-Z]{2,24}$/;
                        if (!emailRegex.test(value)) {
                          return Promise.reject(
                            new Error(
                              lang("Please_enter_valid_email") ||
                                "Please enter a valid email",
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="mobile"
                  label="Primary Phone"
                  help={
                    phoneCountryIso
                      ? `Example: ${getPhoneExample(phoneCountryIso)}`
                      : null
                  }
                  rules={[
                    { required: true, message: "Please enter phone number" },
                    () => ({
                      validator(_, value) {
                        if (!value || !phoneCountryIso) return Promise.resolve();
                        const valid = isValidPhoneNumber(value, phoneCountryIso);
                        return valid
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Invalid phone number for selected country"),
                            );
                      },
                    }),
                  ]}
                >
                  <PhoneInput
                    country="in"
                    enableSearch
                    value={form.getFieldValue("mobile")}
                    onChange={(value, country) => {
                      setCountryCode(country.dialCode);
                      setPhoneCountryIso(country.countryCode.toUpperCase());
                      form.setFieldsValue({ mobile: value.replace(/\+/g, "") });
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                      borderRadius: "8px",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="secondary_mobile" label="Secondary Phone (optional)">
              <PhoneInput
                country="in"
                enableSearch
                value={form.getFieldValue("secondary_mobile")}
                onChange={(value, country) => {
                  setSecondaryCountryCode(country.dialCode);
                  setSecondaryPhoneCountryIso(country.countryCode.toUpperCase());
                  form.setFieldsValue({
                    secondary_mobile: value ? value.replace(/\+/g, "") : "",
                  });
                }}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "8px",
                }}
              />
            </Form.Item>

            <Form.Item
              name="city"
              label="City"
              rules={[
                { required: true, message: "Please enter city" },
                {
                  pattern: /^[A-Za-z0-9 _-]+$/,
                  message: "Invalid characters",
                },
              ]}
            >
              <Input placeholder="Enter city" />
            </Form.Item>

            <Divider orientation="left">Location (Map)</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="latitude"
                  label="Latitude"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input type="number" step="any" placeholder="Latitude" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="longitude"
                  label="Longitude"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input type="number" step="any" placeholder="Longitude" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Select Location on Map">
              <LocationPicker
                onLocationSelect={handleLocationSelect}
                initialPosition={initialPosition}
              />
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => setModalVisible(false)}
                disabled={createLoading || updateLoading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={modalType === "add" ? createLoading : updateLoading}
                disabled={createLoading || updateLoading}
              >
                {modalType === "add" ? "Create" : "Update"}
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
  );
}

export default DoctorManager;
